import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, ChevronRight, BookOpen, FileText, Mail, Video } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProtectedContent from "@/components/ProtectedContent";
import Mandala from "@/components/Mandala";
import { CourseProgressPanel } from "@/components/lms/ModuleProgressTracker";
import { toast } from "sonner";

// ─────────────────────────  Vimeo helpers  ─────────────────────────
// Parse all `https://vimeo.com/<id>` or `https://vimeo.com/<id>/<hash>` URLs from
// raw WP content. The hash is the private-link token Holly uses on her Vimeo Plus plan.
function extractVimeoEmbeds(html, fallbackIds = []) {
  if (!html) {
    return (fallbackIds || []).map((id) => ({ id: String(id), h: null }));
  }
  const found = new Map(); // id -> hash
  const re = /https?:\/\/(?:www\.)?vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    const id = m[1];
    if (!found.has(id) || (!found.get(id) && m[2])) found.set(id, m[2] || null);
  }
  if (found.size === 0) {
    return (fallbackIds || []).map((id) => ({ id: String(id), h: null }));
  }
  return Array.from(found.entries()).map(([id, h]) => ({ id, h }));
}

// Remove the entire `<!-- wp:embed ... --> … <!-- /wp:embed -->` block + any naked
// vimeo URLs left behind. We render embeds ourselves above the content.
function stripVimeoBlocks(html) {
  if (!html) return "";
  let out = html;
  // Remove wp:embed blocks
  out = out.replace(/<!--\s*wp:embed[\s\S]*?<!--\s*\/wp:embed\s*-->/g, "");
  // Remove <figure class="...wp-block-embed..."> blocks (already-rendered form)
  out = out.replace(/<figure[^>]*wp-block-embed[\s\S]*?<\/figure>/g, "");
  // Strip naked vimeo URLs left as plain text (anchors with vimeo url too)
  out = out.replace(/<a[^>]*href="https?:\/\/(?:www\.)?vimeo\.com\/[^"]*"[^>]*>[\s\S]*?<\/a>/g, "");
  out = out.replace(/https?:\/\/(?:www\.)?vimeo\.com\/\d+(?:\/[a-zA-Z0-9]+)?/g, "");
  // Strip dev.avianayoga.com dead links (old WP staging domain)
  out = out.replace(/<a[^>]*href="https?:\/\/dev\.avianayoga\.com[^"]*"[^>]*>([\s\S]*?)<\/a>/g, "<em>$1</em>");
  return out;
}

// Detect which Module-3 widget a given lesson/topic title corresponds to.
const TITLE = (s) => (s || "").toLowerCase().trim();
function getModuleWidget(title) {
  const t = TITLE(title);
  if (t.includes("self-eval") || t.includes("self eval") || t.includes("self-evaluation")) return "self";
  if (t === "hours" || t.includes("hours log") || t.includes("teaching hours") || t.includes("hour log")) return "hours";
  if (t.startsWith("student eval") || t.includes("instructor evaluation") || t.includes("student-eval")) return "student";
  if (t.includes("video submission") || t.includes("teaching video")) return "video";
  return null;
}

// Prettify lesson titles like "01 Foundations" → "Module 1 Foundations".
function prettyLessonTitle(title) {
  if (!title) return title;
  const m = title.match(/^(\d{1,2})\s+(.+)$/);
  if (m) return `Module ${parseInt(m[1], 10)} ${m[2]}`;
  return title;
}

// Split an asana-tutorial-style content_html into pose blocks: each block has
// { title, video: {id,h} | null }. Returns [] when no H2s present.
function splitPoseBlocks(html) {
  if (!html) return [];
  const h2re = /<h2[^>]*>([\s\S]*?)<\/h2>/g;
  const matches = [];
  let m;
  // Decode common HTML entities so titles like "Video &mdash; Subtitle" render cleanly.
  const decodeEntities = (s) => {
    if (typeof window === "undefined" || typeof document === "undefined") return s;
    const el = document.createElement("textarea");
    el.innerHTML = s;
    return el.value;
  };
  while ((m = h2re.exec(html)) !== null) {
    const raw = m[1].replace(/<[^>]+>/g, "").trim();
    matches.push({ title: decodeEntities(raw), start: m.index, end: m.index + m[0].length });
  }
  if (matches.length === 0) return [];
  const blocks = [];
  for (let i = 0; i < matches.length; i++) {
    const segStart = matches[i].end;
    const segEnd = i + 1 < matches.length ? matches[i + 1].start : html.length;
    const segment = html.slice(segStart, segEnd);
    const vimeos = extractVimeoEmbeds(segment, []);
    blocks.push({ title: matches[i].title, video: vimeos[0] || null });
  }
  return blocks;
}

// Detect lessons/topics where the "Mark complete" button should be hidden:
//   • parent module wrapper lessons (no own content, just topics underneath)
//   • Google Meet-up Q&A (optional, mailto CTA instead)
//   • Final "Congratulations" lesson
function shouldHideMarkComplete({ kind, title, embeds, cleanHtml, hasChildren }) {
  if (kind === "quiz") return true;
  const t = (title || "").toLowerCase().trim();
  if (t.includes("google meet")) return true;
  if (t.startsWith("congratulations")) return true;
  if (kind === "lesson" && hasChildren) return true; // parent wrapper
  const textOnly = (cleanHtml || "").replace(/<[^>]+>/g, "").trim();
  if ((!embeds || embeds.length === 0) && !textOnly) return true;
  return false;
}

// Per-pose mark-complete persists in localStorage (no backend schema change).
function readPoseComplete(topicWpId, idx) { try { return localStorage.getItem(`aviana:pose:${topicWpId}:${idx}`) === "1"; } catch { return false; } }
function writePoseComplete(topicWpId, idx, v) { try { localStorage.setItem(`aviana:pose:${topicWpId}:${idx}`, v ? "1" : "0"); } catch {} }

function PoseBlock({ topicWpId, idx, pose }) {
  const [done, setDone] = useState(() => readPoseComplete(topicWpId, idx));
  return (
    <section className="border-t border-aviana-border pt-10" data-testid={`pose-${idx}`}>
      <h3 className="font-display text-2xl md:text-3xl text-aviana-ink mb-5">{pose.title}</h3>
      {pose.video && <VimeoEmbed id={pose.video.id} h={pose.video.h} title={pose.title} />}
      <div className="mt-5">
        <button
          onClick={() => { const n = !done; setDone(n); writePoseComplete(topicWpId, idx, n); toast.success(n ? "Pose marked complete" : "Marked incomplete"); }}
          className={`text-[11px] tracking-[0.18em] uppercase px-5 py-2.5 rounded-full border transition-colors ${done ? "bg-aviana-gold/15 border-aviana-gold/60 text-aviana-ink" : "border-aviana-border text-aviana-muted hover:text-aviana-ink hover:border-aviana-gold/50"}`}
          data-testid={`pose-mark-${idx}`}
        >
          {done ? <><Check size={12} className="inline-block mr-1" /> Pose complete</> : "Mark pose complete"}
        </button>
      </div>
    </section>
  );
}

function GoogleMeetCard() {
  const subject = encodeURIComponent("Aviana YTT — Google Meet-up Q&A request");
  const body = encodeURIComponent("Hi Holly,\n\nI'd love to set up a 20-minute Google Meet-up call for Q&A as part of my training. Here are a few times that work for me:\n\n— (your name)\n");
  const mailto = `mailto:avianayoga@gmail.com?subject=${subject}&body=${body}`;
  return (
    <div className="mt-8 bg-aviana-plumDeep text-aviana-cream rounded-2xl p-8 border border-aviana-gold/30">
      <p className="overline text-aviana-goldSoft mb-3">Optional</p>
      <h3 className="font-display text-2xl text-aviana-cream">Request a 20-minute Q&amp;A with Holly</h3>
      <p className="mt-3 font-serif italic text-aviana-cream/85 text-[15px]">
        This Google Meet-up is offered as an optional companion to your training. Email Holly with a few times that work for you and she'll send back a calendar invite.
      </p>
      <a href={mailto} className="btn-gold !py-2.5 !px-5 !text-[11px] mt-6 inline-flex" data-testid="google-meet-cta">
        <Mail size={14} /> Email Holly to schedule
      </a>
    </div>
  );
}

function VimeoEmbed({ id, h, title }) {
  const src = `https://player.vimeo.com/video/${id}${h ? `?h=${h}&` : "?"}title=0&byline=0&portrait=0&dnt=1`;
  return (
    <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black ring-1 ring-aviana-gold/20" data-testid={`vimeo-${id}`}>
      <iframe
        src={src}
        title={title || `Vimeo ${id}`}
        className="w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

// ─────────────────────────  Module-3 inline CTA cards  ─────────────────────────
function ModuleWidgetCard({ kind, contentKind = "topic", slug, wpId, onComplete, alreadyComplete }) {
  // Map widget kind → display config
  const cfg = {
    self: {
      label: "Self-Evaluation",
      title: "Submit your self-evaluations",
      sub: "Reflect on three different classes you taught — be Bold, be Confident, be Fully Present.",
      to: "/dashboard/self-evaluations",
      cta: "Open self-evaluations →",
    },
    hours: {
      label: "Teaching Hours",
      title: "Log your practice teaching hours",
      sub: "The final piece of your certification — log each session as you teach.",
      to: "/dashboard/teaching-hours",
      cta: "Open hours log →",
    },
    student: {
      label: "Student Evaluations",
      title: "Share your evaluation link",
      sub: "Send this link to the students you teach so they can submit feedback — it counts automatically.",
      to: "/dashboard/student-evaluations",
      cta: "Open student evaluations →",
    },
    video: {
      label: "Video Submission",
      title: "Submit your practice teaching video",
      sub: "", // VideoSubmissionInline computes its own duration-aware copy
      to: null, // handled inline below
      cta: "Submit video link",
    },
  }[kind];
  if (!cfg) return null;

  if (kind === "video") {
    return <VideoSubmissionInline wpId={wpId} contentKind={contentKind} onComplete={() => onComplete?.(contentKind, wpId)} alreadyComplete={alreadyComplete} />;
  }

  return (
    <div className="mt-8 bg-aviana-plumDeep text-aviana-cream rounded-2xl p-8 border border-aviana-gold/30">
      <p className="overline text-aviana-goldSoft mb-3">{cfg.label}</p>
      <h3 className="font-display text-2xl text-aviana-cream">{cfg.title}</h3>
      <p className="mt-3 font-serif italic text-aviana-cream/85 text-[15px]">{cfg.sub}</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to={cfg.to} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid={`widget-cta-${kind}`}>
          {cfg.cta} <ArrowRight size={14} />
        </Link>
        {wpId && (
          <button
            onClick={() => onComplete?.(contentKind, wpId)}
            disabled={alreadyComplete}
            className="btn-secondary !py-2.5 !px-5 !text-[11px] !bg-transparent !text-aviana-cream !border-aviana-cream/30"
            data-testid={`widget-mark-${kind}`}
          >
            {alreadyComplete ? <><Check size={14} /> Marked complete</> : <>Mark this lesson complete <Check size={14} /></>}
          </button>
        )}
      </div>
    </div>
  );
}

function VideoSubmissionInline({ wpId, contentKind = "topic", onComplete, alreadyComplete }) {
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Aerial YTT lesson is 18-25 minutes; Restorative video-submission topic is 10-20 minutes.
  const isAerialYtt = String(window.__avianaCourseWpId || "") === "1954";
  const duration = isAerialYtt ? "18 – 25 minute" : "10 – 20 minute";

  const submit = async (e) => {
    e.preventDefault();
    if (!/^https?:\/\/.+/.test(url.trim())) { toast.error("Please paste a valid video link"); return; }
    setBusy(true);
    try {
      // Look up the course's wp_post_id from the parent context via a hash on the url path
      const courseWpId = String(window.__avianaCourseWpId || "");
      await api.post("/lms/submission", {
        course_wp_id: courseWpId,
        module_key: "module_practice_teaching_video",
        external_url: url.trim(),
        text_content: notes.trim() || null,
      });
      toast.success("Video submitted — Holly will review and email you.");
      setSubmitted(true);
      onComplete?.();
    } catch { toast.error("Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-8 bg-aviana-plumDeep text-aviana-cream rounded-2xl p-8 border border-aviana-gold/30">
      <p className="overline text-aviana-goldSoft mb-3">Video Submission</p>
      <h3 className="font-display text-2xl text-aviana-cream">Submit your practice teaching video</h3>
      <p className="mt-3 font-serif italic text-aviana-cream/85 text-[15px]">Upload a {duration} teaching video to YouTube (unlisted) or Vimeo, then paste the link below.</p>

      {submitted ? (
        <p className="mt-6 text-aviana-goldSoft">✓ Submitted. You'll get an email when Holly has reviewed it.</p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-[11px] tracking-[0.18em] uppercase text-aviana-goldSoft">Video link *</span>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
              className="mt-2 w-full bg-aviana-plumDeep/60 border border-aviana-gold/30 rounded-lg px-4 py-3 outline-none text-aviana-cream placeholder:text-aviana-cream/40 focus:border-aviana-gold/60"
              data-testid="video-submission-url"
            />
          </label>
          <label className="block">
            <span className="text-[11px] tracking-[0.18em] uppercase text-aviana-goldSoft">Notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything Holly should know about this class…"
              className="mt-2 w-full bg-aviana-plumDeep/60 border border-aviana-gold/30 rounded-lg px-4 py-3 outline-none text-aviana-cream placeholder:text-aviana-cream/40 focus:border-aviana-gold/60"
              data-testid="video-submission-notes"
            />
          </label>
          <button type="submit" disabled={busy} className="btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="video-submission-submit">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <>Submit for review <ArrowRight size={14} /></>}
          </button>
        </form>
      )}
      {alreadyComplete && submitted && <p className="mt-4 text-aviana-goldSoft text-[12px]">Already marked complete.</p>}
    </div>
  );
}

export default function CoursePlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tree, setTree] = useState(null);
  const [activeKey, setActiveKey] = useState(null); // "topic:WP_ID" | "lesson:WP_ID" | "quiz:WP_ID"
  const [activeContent, setActiveContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user === false) {
      navigate("/login", { state: { from: window.location.pathname } });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/lms/course/${slug}`);
        setTree(data);
        // expose course wp_post_id globally so VideoSubmissionInline can read it without prop drilling
        if (typeof window !== "undefined") window.__avianaCourseWpId = data.course?.wp_post_id;
        // pre-select first lesson
        const first = data.lessons[0];
        if (first) {
          const firstTopic = first.topics?.[0];
          if (firstTopic) {
            setActiveKey(`topic:${firstTopic.wp_post_id}`);
            setActiveContent(firstTopic);
          } else {
            setActiveKey(`lesson:${first.wp_post_id}`);
            setActiveContent(first);
          }
        }
      } catch (e) {
        if (e.response?.status === 403) setError("not_enrolled");
        else setError("load_failed");
      } finally { setLoading(false); }
    })();
  }, [slug, user]);

  const markComplete = async (kind, wpId) => {
    try {
      await api.post(`/lms/progress/${kind}/${wpId}`);
      setTree((t) => ({
        ...t,
        progress: {
          ...t.progress,
          [`completed_${kind}_ids`]: [...(t.progress?.[`completed_${kind}_ids`] || []), wpId],
        },
      }));
      toast.success("Marked complete");
    } catch { toast.error("Could not mark complete"); }
  };

  const contentRef = useRef(null);

  const select = (kind, item) => {
    setActiveKey(`${kind}:${item.wp_post_id}`);
    setActiveContent({ ...item, __kind: kind });
    // On mobile (no lg breakpoint), the content pane stacks BELOW the sidebar
    // so tapping a lesson appears to do nothing — the content renders off-screen.
    // Scroll the content pane into view so mobile students see their lesson.
    requestAnimationFrame(() => {
      if (window.innerWidth < 1024 && contentRef.current) {
        contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  if (loading) return <div className="pt-36 container-aviana"><Loader2 className="animate-spin text-aviana-gold" /></div>;
  if (error === "not_enrolled") return (
    <div className="pt-36 pb-24 min-h-[70vh] flex items-center" data-testid="not-enrolled-page">
      <div className="container-aviana max-w-2xl text-center">
        <Mandala size={120} variant="full" className="text-aviana-gold mx-auto mb-7 opacity-70 animate-spin-slow" />
        <p className="overline mb-3">Not yet enrolled</p>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Join the course to begin.</h1>
        <p className="mt-6 text-aviana-muted">Once you enroll, you'll have lifetime access to all materials.</p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to={`/courses/${slug}`} className="btn-gold">Enroll · $444</Link>
          <Link to="/" className="btn-secondary">Return home</Link>
        </div>
      </div>
    </div>
  );

  if (!tree) return null;

  const completed_topics = new Set(tree.progress?.completed_topic_ids || []);
  const completed_lessons = new Set(tree.progress?.completed_lesson_ids || []);
  const completed_quizzes = new Set(tree.progress?.completed_quiz_ids || []);

  return (
    <div className="pt-24 min-h-screen bg-aviana-surface" data-testid="course-player">
      <div className="container-aviana py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-6">
          <ArrowLeft size={14} /> My dashboard
        </Link>

        <h1 className="font-display text-3xl md:text-5xl text-aviana-ink leading-[1.05] mb-8">{tree.course.title}</h1>
        <CourseProgressPanel courseWpId={tree.course.wp_post_id} refreshKey={JSON.stringify(tree.progress || {})} />

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar — lesson tree */}
          <aside className="lg:col-span-4 xl:col-span-3" data-testid="lesson-sidebar">
            <div className="bg-aviana-cream rounded-2xl border border-aviana-border overflow-hidden sticky top-28">
              <div className="p-5 border-b border-aviana-border">
                <p className="overline-plum">Curriculum</p>
                <p className="text-[12px] text-aviana-muted mt-2">{tree.lessons.length} modules</p>
                <p className="text-[11px] text-aviana-gold/80 mt-2 lg:hidden">Tap a module to open it below ↓</p>
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {tree.lessons.map((lesson) => (
                  <div key={lesson.id} className="border-b border-aviana-border/50 last:border-0">
                    <button
                      onClick={() => select("lesson", lesson)}
                      className={`w-full text-left px-5 py-3 hover:bg-aviana-surface transition-colors ${activeKey === `lesson:${lesson.wp_post_id}` ? "bg-aviana-surface" : ""}`}
                      data-testid={`sidebar-lesson-${lesson.wp_post_id}`}
                    >
                      <div className="flex items-center gap-2">
                        {completed_lessons.has(lesson.wp_post_id) && <Check size={12} className="text-aviana-gold" />}
                        <span className="font-display text-[15px] text-aviana-ink leading-tight flex-1">{prettyLessonTitle(lesson.title)}</span>
                      </div>
                    </button>
                    {(lesson.topics || []).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => select("topic", t)}
                        className={`w-full text-left pl-9 pr-5 py-2 text-[13px] flex items-center gap-2 hover:bg-aviana-surface ${activeKey === `topic:${t.wp_post_id}` ? "text-aviana-plum bg-aviana-surface" : "text-aviana-muted"}`}
                        data-testid={`sidebar-topic-${t.wp_post_id}`}
                      >
                        {completed_topics.has(t.wp_post_id) ? <Check size={12} className="text-aviana-gold" /> : (t.vimeo_ids?.length ? <Video size={12} /> : <FileText size={12} />)}
                        <span className="truncate flex-1">{t.title}</span>
                      </button>
                    ))}
                    {(lesson.quizzes || []).map((q) => (
                      <button
                        key={q.id}
                        onClick={() => select("quiz", q)}
                        className={`w-full text-left pl-9 pr-5 py-2 text-[13px] flex items-center gap-2 hover:bg-aviana-surface ${activeKey === `quiz:${q.wp_post_id}` ? "text-aviana-plum bg-aviana-surface" : "text-aviana-muted"}`}
                        data-testid={`sidebar-quiz-${q.wp_post_id}`}
                      >
                        {completed_quizzes.has(q.wp_post_id) ? <Check size={12} className="text-aviana-gold" /> : <BookOpen size={12} />}
                        <span className="flex-1">{q.title} <span className="text-aviana-gold/70 text-[10px] ml-1 uppercase tracking-wider">Quiz</span></span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* Main pane */}
          <main ref={contentRef} className="lg:col-span-8 xl:col-span-9 scroll-mt-24" data-testid="course-content-pane">
            {!activeContent && (
              <div className="text-aviana-muted text-center py-32">
                <span className="lg:hidden">Scroll up and tap a module to begin.</span>
                <span className="hidden lg:inline">Select a lesson from the sidebar to begin.</span>
              </div>
            )}

            {activeContent && (() => {
              const isTopic = activeContent.__kind === "topic";
              const isLesson = activeContent.__kind === "lesson";
              const isQuiz = activeContent.__kind === "quiz";
              const titleLower = (activeContent.title || "").toLowerCase();
              const isGoogleMeet = titleLower.includes("google meet");
              const widget = (isTopic || isLesson) ? getModuleWidget(activeContent.title) : null;
              const rawHtml = activeContent.content_html || "";
              const cleanHtml = stripVimeoBlocks(rawHtml);
              const poses = (isTopic || isLesson) ? splitPoseBlocks(rawHtml) : [];
              // Only use pose-block rendering when there are at least 2 H2-separated videos
              const usePoseLayout = poses.length >= 2 && poses.some((p) => p.video);
              const embeds = usePoseLayout ? [] : extractVimeoEmbeds(rawHtml, activeContent.vimeo_ids);
              const completedFlag =
                (isTopic && completed_topics.has(activeContent.wp_post_id)) ||
                (isLesson && completed_lessons.has(activeContent.wp_post_id));
              // Find this lesson in tree (for hasChildren check)
              const lessonRecord = isLesson ? tree.lessons.find((l) => l.wp_post_id === activeContent.wp_post_id) : null;
              const hasChildren = !!(lessonRecord && ((lessonRecord.topics || []).length || (lessonRecord.quizzes || []).length));
              const hideMark = shouldHideMarkComplete({ kind: activeContent.__kind, title: activeContent.title, embeds, cleanHtml, hasChildren });

              return (
                <article className="bg-aviana-cream rounded-2xl border border-aviana-border p-8 md:p-12">
                  <p className="overline-plum mb-3">{isQuiz ? "Quiz" : isTopic ? "Lesson" : "Module"}</p>
                  <h2 className="font-display text-3xl md:text-4xl text-aviana-ink leading-[1.05]">
                    {isLesson ? prettyLessonTitle(activeContent.title) : activeContent.title}
                  </h2>

                  {/* Google Meet-up Q&A optional CTA */}
                  {isGoogleMeet && <GoogleMeetCard />}

                  {/* Pose-by-pose layout (Asana Tutorials) */}
                  {usePoseLayout && (
                    <div className="mt-10 space-y-12" data-testid="pose-list">
                      {poses.map((p, i) => (
                        <PoseBlock key={i} topicWpId={activeContent.wp_post_id} idx={i} pose={p} />
                      ))}
                    </div>
                  )}

                  {/* Standard single-or-list Vimeo embeds (with security hash when available) */}
                  {!usePoseLayout && embeds.length > 0 && (
                    <div className="mt-8 space-y-6" data-testid="vimeo-list">
                      {embeds.map((e) => (
                        <VimeoEmbed key={e.id} id={e.id} h={e.h} title={activeContent.title} />
                      ))}
                    </div>
                  )}

                  {/* Module-3 inline widget (replaces dead links) */}
                  {widget && (
                    <ModuleWidgetCard
                      kind={widget}
                      contentKind={activeContent.__kind}
                      slug={slug}
                      wpId={activeContent.wp_post_id}
                      onComplete={markComplete}
                      alreadyComplete={completedFlag}
                    />
                  )}

                  {/* Stripped content (no embed blocks, no dead-link anchors) — skip when using pose layout (titles already shown) */}
                  {!usePoseLayout && cleanHtml && cleanHtml.replace(/<[^>]+>/g, "").trim() && (
                    <ProtectedContent className="mt-8">
                      <div className="aviana-richtext text-aviana-inkSoft leading-[1.85]" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
                    </ProtectedContent>
                  )}
                  {/* Suppress `intro_html` on the LESSON page for quiz-type items — the same
                      description is shown on the quiz page itself, and (for single-essay quizzes
                      like the Research Paper) also inside the essay question. Rendering it here
                      duplicates the text. */}
                  {activeContent.intro_html && !isQuiz && (
                    <ProtectedContent className="mt-8">
                      <div className="aviana-richtext text-aviana-inkSoft leading-[1.85]" dangerouslySetInnerHTML={{ __html: activeContent.intro_html }} />
                    </ProtectedContent>
                  )}

                  {isQuiz && (
                    <div className="mt-10">
                      <Link to={`/courses/${slug}/quiz/${activeContent.id}`} className="btn-gold" data-testid={`take-quiz-${activeContent.wp_post_id}`}>
                        Begin Quiz <ChevronRight size={16} />
                      </Link>
                    </div>
                  )}

                  {!isQuiz && !widget && !hideMark && (
                    <div className="mt-10 flex items-center justify-between">
                      <button
                        onClick={() => markComplete(activeContent.__kind, activeContent.wp_post_id)}
                        disabled={completedFlag}
                        className="btn-gold disabled:opacity-50"
                        data-testid="mark-complete-btn"
                      >
                        {completedFlag ? <><Check size={16} /> Completed</> : <>Mark complete <Check size={16} /></>}
                      </button>
                    </div>
                  )}

                  {/* Submission form — rendered when a lesson opts in via
                      `submission_module_key` (currently used for Module 4's
                      class-audit video). Creates an lms_submission that
                      Holly reviews from Admin → Submissions. */}
                  {isLesson && activeContent.submission_module_key && (
                    <LessonSubmissionForm
                      lessonWpId={activeContent.wp_post_id}
                      courseWpId={tree.course.wp_post_id}
                      moduleKey={activeContent.submission_module_key}
                      prompt={activeContent.submission_prompt || "Paste your submission link below."}
                    />
                  )}
                </article>
              );
            })()}
          </main>
        </div>
      </div>
    </div>
  );
}

/**
 * Stand-alone quiz taking page.
 */
export function QuizPlayer() {
  const { slug, quizId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lms/quiz/${quizId}`);
        setData(data);
      } catch { toast.error("Could not load quiz"); }
    })();
  }, [quizId]);

  const [missing, setMissing] = useState(new Set()); // question ids the student left blank on last submit

  const isAnswered = (q) => {
    const a = answers[q.id];
    if (q.question_type === "essay" || q.question_type === "survey") {
      return typeof a === "string" && a.trim().length > 0;
    }
    return a !== undefined && a !== null && a !== "";
  };

  const submit = async () => {
    // Client-side "all required" gate — every question is required unless
    // it explicitly opts out via q.optional === true. This mirrors Holly's
    // rule for Module 1 exam + Module 3 self-evals.
    if (data) {
      const blanks = new Set(
        data.questions.filter((q) => !q.optional && !isAnswered(q)).map((q) => q.id)
      );
      if (blanks.size > 0) {
        setMissing(blanks);
        toast.error(`Please answer all ${blanks.size} remaining question${blanks.size === 1 ? "" : "s"} before submitting.`);
        // Scroll to first unanswered
        const first = document.querySelector(`[data-testid="quiz-q-${[...blanks][0]}"]`);
        if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/lms/quiz/${quizId}/attempt`, { answers });
      setResult(res);
      if (res.passed) toast.success("You passed!");
    } catch (e) { toast.error("Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (!data) return <div className="pt-36 container-aviana"><Loader2 className="animate-spin text-aviana-gold" /></div>;

  return (
    <div className="pt-32 pb-24 bg-aviana-surface min-h-screen" data-testid="quiz-player">
      <div className="container-aviana max-w-3xl">
        <Link to={`/courses/${slug}/learn`} className="text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink">← Back to course</Link>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05] mt-6">{data.quiz.title}</h1>
        {/* Only render the quiz intro when it adds value: skip it when the quiz has a
            single essay question, because that question's own `question_html` already
            contains the full prompt (this is how the Research Paper is authored). */}
        {data.quiz.intro_html && !(data.questions.length === 1 && data.questions[0].question_type === "essay") && (
          <div className="mt-6 aviana-richtext text-aviana-muted" dangerouslySetInnerHTML={{ __html: data.quiz.intro_html }} />
        )}

        {result ? (
          <div className="mt-12 bg-aviana-cream rounded-2xl p-10 border border-aviana-border" data-testid="quiz-result">
            <div className="text-center">
              <Mandala size={64} variant={result.passed ? "sun" : "rose"} className="text-aviana-gold mx-auto mb-4" />
              <h2 className="font-display text-3xl text-aviana-ink">
                {result.passed ? "You passed." : result.needs_grading ? "Submitted for grading." : "Almost there."}
              </h2>
              <p className="mt-3 text-aviana-muted">
                Score: <strong className="text-aviana-ink">{result.earned} / {result.total_points}</strong> ({result.score_pct}%)
              </p>
              {!result.passed && !result.needs_grading && (
                <p className="mt-4 text-aviana-muted text-sm">A passing score is 75%. You may retake this quiz.</p>
              )}
              {!result.passed && result.needs_grading && (
                <p className="mt-4 text-aviana-muted text-sm">Holly will review your essay answers and confirm your completion. You'll receive an email once she's done.</p>
              )}
            </div>

            {/* Student's own missed-questions breakdown — so they know
                exactly which questions to revisit before retaking + can
                read Holly's feedback once she leaves it. */}
            <StudentMissedBreakdown breakdown={result.breakdown} questionFeedback={result.question_feedback} />

            <div className="mt-8 flex gap-4 justify-center">
              {!result.passed && !result.needs_grading && (
                <button onClick={() => { setResult(null); setAnswers({}); }} className="btn-secondary">Retake</button>
              )}
              <button onClick={() => navigate(`/courses/${slug}/learn`)} className="btn-gold">Back to course</button>
            </div>
          </div>
        ) : (
          <div className="mt-10 space-y-8">
            {data.questions.map((q, idx) => {
              const isMissing = missing.has(q.id);
              const isRequired = !q.optional;
              return (
              <article
                key={q.id}
                className={`bg-aviana-cream rounded-2xl p-8 border transition-colors ${isMissing ? "border-red-400 ring-2 ring-red-200" : "border-aviana-border"}`}
                data-testid={`quiz-q-${q.id}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <p className="overline">Question {idx + 1}</p>
                  {isRequired && (
                    <span className="text-[9px] tracking-[0.18em] uppercase bg-aviana-plum/10 text-aviana-plum rounded-full px-2 py-0.5">
                      Required
                    </span>
                  )}
                  {isMissing && (
                    <span className="text-[9px] tracking-[0.18em] uppercase bg-red-100 text-red-700 rounded-full px-2 py-0.5">
                      Please answer
                    </span>
                  )}
                </div>
                <div className="aviana-richtext text-aviana-ink" dangerouslySetInnerHTML={{ __html: q.question_html || q.title || "" }} />
                {q.question_type === "essay" || q.question_type === "survey" ? (
                  <textarea
                    value={answers[q.id] || ""}
                    onChange={(e) => {
                      setAnswers((a) => ({ ...a, [q.id]: e.target.value }));
                      if (isMissing) setMissing((m) => { const n = new Set(m); n.delete(q.id); return n; });
                    }}
                    className="w-full mt-5 bg-white border border-aviana-border rounded-lg px-4 py-3 outline-none focus:border-aviana-gold/60"
                    rows={q.question_type === "essay" ? 6 : 3}
                    placeholder="Your answer…"
                    data-testid={`essay-${q.id}`}
                  />
                ) : (
                  <div className="mt-5 space-y-2">
                    {q.choices.length === 0 ? (
                      <p className="text-aviana-gold text-sm">⚠ This question has no answer choices yet — please contact Holly.</p>
                    ) : q.choices.map((c, cIdx) => (
                      <label
                        key={cIdx}
                        className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${answers[q.id] === cIdx ? "border-aviana-gold bg-aviana-gold/5" : "border-aviana-border hover:border-aviana-gold/40"}`}
                        data-testid={`quiz-choice-${q.id}-${cIdx}`}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={cIdx}
                          checked={answers[q.id] === cIdx}
                          onChange={() => {
                            setAnswers((a) => ({ ...a, [q.id]: cIdx }));
                            if (isMissing) setMissing((m) => { const n = new Set(m); n.delete(q.id); return n; });
                          }}
                          className="text-aviana-gold"
                        />
                        <span className="text-aviana-ink">{c.text}</span>
                      </label>
                    ))}
                  </div>
                )}
              </article>
              );
            })}
            <div className="flex justify-center pt-6">
              <button onClick={submit} disabled={submitting} className="btn-gold" data-testid="submit-quiz-btn">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : "Submit quiz"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Shown to the student on their quiz-attempt result screen. Lists every
// MCQ question they missed with Holly's per-question feedback (if she has
// left any). The intent: turn every failed attempt into a learning moment
// with a concrete "here's the correct answer + why" note.
function StudentMissedBreakdown({ breakdown, questionFeedback }) {
  if (!Array.isArray(breakdown)) return null;
  const missed = breakdown.filter((b) => b && b.is_correct === false);
  if (missed.length === 0) return null;
  const feedback = questionFeedback || {};
  return (
    <div className="mt-8 text-left" data-testid="student-missed-breakdown">
      <p className="overline-plum mb-3">Review your missed questions</p>
      <ul className="space-y-3">
        {missed.map((b, i) => {
          const labels = Array.isArray(b.choice_labels) ? b.choice_labels : [];
          let picked = "—";
          if (typeof b.submitted === "number") picked = labels[b.submitted] || `Choice ${b.submitted + 1}`;
          else if (Array.isArray(b.submitted)) picked = b.submitted.map((v) => labels[v] || `Choice ${v + 1}`).join(", ");
          const correct = (Array.isArray(b.correct_idx) ? b.correct_idx : [])
            .map((v) => labels[v])
            .filter(Boolean)
            .join(", ");
          const qid = b.question_id;
          const note = feedback[qid];
          return (
            <li key={qid || i} className="bg-white border border-aviana-border rounded-lg p-4">
              <p
                className="text-aviana-ink font-medium text-[13px]"
                dangerouslySetInnerHTML={{ __html: String(b.question_html || b.question_title || `Question ${i + 1}`) }}
              />
              <p className="text-[12px] mt-2">
                <span className="text-red-700 font-medium">Your answer:</span>{" "}
                <span className="text-aviana-ink">{picked || "(no answer)"}</span>
              </p>
              <p className="text-[12px] mt-1">
                <span className="text-emerald-800 font-medium">Correct answer:</span>{" "}
                <span className="text-aviana-ink">{correct || "(not configured)"}</span>
              </p>
              {note && (
                <p className="text-[12px] mt-3 border-l-2 border-aviana-gold pl-3 text-aviana-plumDeep italic">
                  <span className="not-italic text-aviana-gold text-[10px] tracking-[0.16em] uppercase block mb-1">From Holly</span>
                  {note}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


// Lightweight URL-only submission form. Used on lessons that require the
// student to paste a link (YouTube unlisted video, PDF drive share, etc.)
// so Holly can approve the milestone from Admin → Submissions.
function LessonSubmissionForm({ lessonWpId, courseWpId, moduleKey, prompt }) {
  const [existing, setExisting] = React.useState(null);
  const [url, setUrl] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api.get(`/lms/my-submissions/${encodeURIComponent(courseWpId)}`)
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        setExisting(items.find((s) => s.module_key === moduleKey) || null);
      })
      .catch(() => {});
  }, [courseWpId, moduleKey]);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) { toast.error("Please paste a link before submitting."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/lms/submission", {
        course_wp_id: String(courseWpId),
        module_key: moduleKey,
        external_url: url.trim(),
        text_content: note.trim(),
      });
      setExisting(data);
      toast.success("Submitted — Holly will review and email you once she's approved.");
      setUrl(""); setNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const statusBadge = existing && {
    submitted:      { txt: "Awaiting review",   cls: "bg-aviana-gold/15 text-aviana-plumDeep" },
    approved:       { txt: "Approved ✓",        cls: "bg-emerald-100 text-emerald-800" },
    needs_revision: { txt: "Needs revision",    cls: "bg-orange-100 text-orange-800" },
    rejected:       { txt: "Not accepted",      cls: "bg-red-100 text-red-800" },
  }[existing.status];

  return (
    <div className="mt-10 bg-aviana-cream rounded-2xl p-6 border border-aviana-border" data-testid={`submission-${moduleKey}`}>
      <p className="overline-plum mb-2">Submit your work</p>
      <p className="text-aviana-inkSoft text-[14px] leading-[1.7] mb-4">{prompt}</p>

      {existing && (
        <div className="mb-5 bg-white border border-aviana-border rounded-lg p-4 text-[13px]">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-aviana-muted">Your submission:</span>
            {statusBadge && <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-full ${statusBadge.cls}`}>{statusBadge.txt}</span>}
          </div>
          {existing.external_url && (
            <a href={existing.external_url} target="_blank" rel="noopener noreferrer" className="text-aviana-plum underline break-all">{existing.external_url}</a>
          )}
          {existing.reviewer_notes && (
            <p className="mt-3 border-l-2 border-aviana-gold pl-3 text-aviana-plumDeep italic text-[12px]">
              <span className="not-italic text-aviana-gold text-[10px] tracking-[0.16em] uppercase block mb-1">Note from Holly</span>
              {existing.reviewer_notes}
            </p>
          )}
        </div>
      )}

      {(!existing || existing.status === "needs_revision") && (
        <form onSubmit={submit} className="space-y-3">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtu.be/…  or  https://vimeo.com/…"
            className="w-full bg-white border border-aviana-border rounded-lg px-4 py-3 outline-none focus:border-aviana-gold/60"
            data-testid={`submission-url-${moduleKey}`}
            required
          />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Any notes for Holly (optional)…"
            rows={2}
            className="w-full bg-white border border-aviana-border rounded-lg px-4 py-3 outline-none focus:border-aviana-gold/60 text-[14px]"
          />
          <button type="submit" disabled={busy} className="btn-gold" data-testid={`submission-submit-${moduleKey}`}>
            {busy ? "Sending…" : existing?.status === "needs_revision" ? "Resubmit" : "Submit for review"}
          </button>
        </form>
      )}
    </div>
  );
}
